// Mirrors qc/serializers.py (QCReviewModelSerializer / QCRunModelSerializer) and
// qc/constants.py in tpn-admin.
export type QCVerdict = 'agree' | 'disagree' | 'uncertain' | 'not_evaluated';
export type QCErrorClass =
  | 'marking_contradicts_findings'
  | 'contradicts_evidence'
  | 'contradicts_sp_answer'
  | 'spelling_quality'
  | 'none';
export type QCReviewType = 'assessment' | 'remediation';
export type QCRunMode = 'production' | 'evaluation';
export type QCRunStatus = 'pending' | 'running' | 'complete' | 'failed';

// A QCReview's target is one of two grains (CONTEXT.md: "Line-items vs aggregate call"):
// sub_control (assessor_question_answer set, answer_id resolves to the sub-control Answer)
// or rollup (assessor_answer set directly, answer_id null - the Question-level roll-up
// verdict). Exactly one of assessor_question_answer/assessor_answer is set per row;
// question_id/assessor_answer_id always resolve regardless of which grain the row is.
export type QCTargetGrain = 'sub_control' | 'rollup';

// The admin's reaction to a QC finding - the ADR-0005 agreement-rate signal. This is NOT the
// admin's disposition of the assessor's answer (VerificationStatus below) - see ADR-0007.
export type QCAdminResponse = 'accept' | 'edit' | 'reject';

export const QC_ADMIN_RESPONSES = {
  ACCEPT: 'accept',
  EDIT: 'edit',
  REJECT: 'reject',
} as const;

export const QC_ADMIN_RESPONSE_LABELS: Record<QCAdminResponse, string> = {
  accept: 'Yes',
  edit: 'Edit',
  reject: 'No',
};

// AssessorAnswer.verification_status (utils.model_mixins.VerificationStatusMixin) - the
// admin's disposition of the ASSESSOR'S ANSWER. This is the persisted, pre-existing "final
// human call" that QC output feeds but never writes (CONTEXT.md, ADR-0002), and it is what
// makes a review unit "done" for the QC review queue (CONTEXT.md: Attention). Orthogonal to
// QCAdminResponse above - see ADR-0007.
export type VerificationStatus = 'reviewed' | 'rejected' | 'not_started';

export const VERIFICATION_STATUSES = {
  REVIEWED: 'reviewed',
  REJECTED: 'rejected',
  NOT_STARTED: 'not_started',
} as const;

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  reviewed: 'Reviewed',
  rejected: 'Rejected',
  not_started: 'Not Started',
};

// GET /qc/workflows/<workflow_id>/reviews/ — one row per QC verdict, at either target grain.
// Field names are camelCase to match the wire format: djangorestframework-camel-case
// (CamelCaseJSONRenderer, tpn/settings.py DEFAULT_RENDERER_CLASSES) transforms every DRF
// response, including this one — there is no per-endpoint opt-out (tpn-admin CLAUDE.md:
// "API casing"). This interface previously mirrored qc/serializers.py's snake_case Python
// field names directly instead of the actual JSON keys, so every field read here was
// silently undefined at runtime.
export interface QCReview {
  id: number;
  qcRunId: number;
  targetGrain: QCTargetGrain;
  assessorQuestionAnswer: number | null;
  assessorAnswer: number | null;
  questionId: number;
  questionTitle: string;
  assessorAnswerId: number;
  answerId: number | null;
  verdict: QCVerdict;
  errorClass: QCErrorClass | null;
  confidence: number | null;
  rationale: string;
  suspectedInjection: boolean;
  isCurrent: boolean;
  adminResponse: QCAdminResponse | null;
  adminResponseAt: string | null;
  modelVersion: string;
  promptVersion: string;
  createdAt: string;
  updatedAt: string;
}

// POST /qc/workflows/<workflow_id>/run/ response body.
export interface QCRun {
  id: number;
  reviewType: QCReviewType;
  mode: QCRunMode;
  status: QCRunStatus;
  modelVersion: string;
  promptVersion: string;
  triggeredBy: number | null;
  createdAt: string;
  updatedAt: string;
}

// Verdicts that mean "look here first" for a human (qc/constants.py: QC_FLAGGED_VERDICTS).
export const QC_FLAGGED_VERDICTS: QCVerdict[] = ['disagree', 'uncertain'];
